import type { SavedGameState } from '../shared/types.js';
import { submarineToMapPoint } from '../shared/spatial.js';
import { state as controllerState } from './controllerState.js';
import { getMapByMissionId } from './rendering/maps/index.js';

/** Resolves the label of the docking port the submarine is currently nearest to, if any. */
export const getDockingPortLabel = (): string | null => {
  if (!controllerState.lastRoomState || !controllerState.lastSnapshot) return null;

  const currentMap = getMapByMissionId(controllerState.lastRoomState.mission);
  if (!currentMap?.glbModelMarkers?.length) return null;

  const submarinePoint = submarineToMapPoint(controllerState.lastSnapshot.submarine);
  let nearest: { markerLabel: string | null; distance: number } = { markerLabel: null, distance: Infinity };

  for (const marker of currentMap.glbModelMarkers) {
    if (marker.model.toLowerCase() !== 'docking_port') continue;

    const dx = marker.mapX - submarinePoint.x;
    const dz = marker.mapZ - submarinePoint.z;
    const dy = -marker.depth - (-controllerState.lastSnapshot.submarine.depth);
    const distance = Math.hypot(dx, dz, dy);
    if (distance < nearest.distance) {
      nearest = { markerLabel: marker.label ?? marker.model ?? null, distance };
    }
  }

  return nearest.markerLabel;
};

type CreateSavedGameStateOptions = {
  getRevealedNodes: () => Set<string>;
};

/**
 * Builds a serialisable save snapshot from the current controller state. Returns null unless the
 * submarine is docked (saving is only permitted while docked).
 */
export const createSavedGameState = ({ getRevealedNodes }: CreateSavedGameStateOptions): SavedGameState | null => {
  if (!controllerState.lastRoomState || !controllerState.lastSnapshot) return null;
  if (!controllerState.lastSnapshot.submarine.docked) return null;

  return {
    version: 1,
    mission: controllerState.lastRoomState.mission,
    savedAt: new Date().toISOString(),
    dockingPortLabel: getDockingPortLabel(),
    mapTile: null,
    submarine: controllerState.lastSnapshot.submarine,
    cargo: controllerState.lastRoomState.cargo,
    craftQueue: controllerState.lastRoomState.craftQueue,
    autoCraft: controllerState.lastRoomState.autoCraft,
    wares: controllerState.lastRoomState.wares,
    ownedUpgrades: controllerState.lastRoomState.ownedUpgrades,
    drones: controllerState.lastSnapshot.drones ?? [],
    resourceNodes: controllerState.lastSnapshot.world.resourceNodes ?? [],
    nodeRevealState: getRevealedNodes().size > 0 ? JSON.stringify([...getRevealedNodes()]) : undefined,
    metricHistory: controllerState.lastRoomState.metricHistory ?? [],
    credits: controllerState.lastRoomState.credits,
    facilityPrices: controllerState.lastRoomState.facilityPrices,
    difficulty: controllerState.lastRoomState.difficulty,
    hullZones: controllerState.lastSnapshot.submarine.hullZones,
    activeMissionIndex: controllerState.lastRoomState.activeMissionIndex,
    script: controllerState.lastRoomState.missionScript,
    campaign: controllerState.lastRoomState.campaign
      ? {
          campaignId: controllerState.lastRoomState.campaign.campaignId,
          chapterIndex: controllerState.lastRoomState.campaign.chapterIndex,
          completedChapterIds: controllerState.lastRoomState.campaign.completedChapterIds,
          flags: controllerState.lastRoomState.campaign.flags,
          logsFound: controllerState.lastRoomState.campaign.logsFound
        }
      : undefined
  };
};

/** Triggers a browser download of the supplied save snapshot as a JSON file; returns the file name. */
export const downloadSavedGame = (savedGame: SavedGameState): string => {
  const blob = new Blob([JSON.stringify(savedGame, null, 2)], { type: 'application/json' });
  const fileName = `aegis7_save_${controllerState.currentRoomCode || 'room'}_${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return fileName;
};
